import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { API_URL } from '../config';
import { toast } from 'react-toastify';

function AdminDashboard() {
    const [users, setUsers] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'users'
    
    // User Management State
    const [search, setSearch] = useState('');
    const [filterRole, setFilterRole] = useState(''); 
    const [filterStatus, setFilterStatus] = useState('');
    const [totalUsers, setTotalUsers] = useState(0);
    
    // Ban Modal State
    const [showBanModal, setShowBanModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [banReason, setBanReason] = useState('');
    const [processing, setProcessing] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();

    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    const userEmail = localStorage.getItem('email');

    useEffect(() => {
        if (!token || role !== 'ADMIN') {
            navigate('/');
            return;
        }
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, filterRole, filterStatus]);

    const fetchData = async () => {
        setLoading(true);
        try {
            let userUrl = `${API_URL}/api/admin/users?`;
            if (search) userUrl += `search=${search}&`;
            if (filterRole) userUrl += `role=${filterRole}&`;
            if (filterStatus) userUrl += `enabled=${filterStatus === 'active' ? 'true' : 'false'}&`;

            const usersRes = await axios.get(userUrl, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            setUsers(usersRes.data.content || []);
            setTotalUsers(usersRes.data.totalElements || 0);

            const campRes = await axios.get(`${API_URL}/api/admin/campaign-requests`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCampaigns(campRes.data.content || []);

            setLoading(false);
        } catch (err) {
            toast.error('Failed to load data');
            setLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.clear();
        toast.success('Logged out successfully');
        navigate('/');
    };

    const openBanModal = (user) => {
        setSelectedUser(user);
        setBanReason('');
        setShowBanModal(true);
    };

    const handleToggleBan = async () => {
        if (!selectedUser) return;
        if (selectedUser.enabled && !banReason.trim()) {
            toast.error('Please provide a reason for banning this user.');
            return;
        }

        setProcessing(true);
        try {
            await axios.put(`${API_URL}/api/admin/users/${selectedUser.id}/enable`, 
                { 
                    enabled: !selectedUser.enabled,
                    reason: banReason || 'Admin action'
                }, 
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`User ${selectedUser.enabled ? 'banned' : 'unbanned'} successfully`);
            setShowBanModal(false);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to update user');
        }
        setProcessing(false);
    };

    const handleDeleteUser = async (userId) => {
        if (window.confirm('Are you sure you want to permanently delete this user? This will anonymize their account.')) {
            try {
                await axios.delete(`${API_URL}/api/admin/users/${userId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('User deleted successfully');
                fetchData();
            } catch (err) {
                toast.error(err.response?.data?.message || 'Failed to delete user');
            }
        }
    };

    return (
        <div className="dashboard-layout">
            {/* SIDEBAR */}
            <aside className="sidebar" style={{ borderRight: '1px solid var(--error)' }}>
                <Link to="/" className="sidebar-brand">infloir <span style={{ color: 'var(--error)', fontSize: '12px' }}>ADMIN</span></Link>
                <nav className="sidebar-nav">
                    <Link to="/admin-dashboard" className={`nav-item ${location.pathname === '/admin-dashboard' ? 'active' : ''}`}>
                        <span>🛡️</span> Overview
                    </Link>
                    <Link to="/admin/payouts" className="nav-item">
                        <span>💸</span> Manage Payouts
                    </Link>
                    <Link to="/admin/revenue" className="nav-item">
                        <span>💰</span> Revenue
                    </Link>
                </nav>
                <div style={{ marginTop: 'auto' }}>
                    <button onClick={handleLogout} className="nav-item" style={{ width: '100%', background: 'transparent', border: 'none', textAlign: 'left', cursor: 'pointer' }}>
                        <span>🚪</span> Log out
                    </button>
                </div>
            </aside>

            {/* MAIN CONTENT */}
            <div className="main-content">
                <header className="topbar">
                    <h2 style={{ fontSize: '18px', fontWeight: '500' }}>Admin Control Center</h2>
                    <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{userEmail}</span>
                </header>

                <main className="page-container page-fade-in">
                    
                    {/* MAIN TABS */}
                    <div style={{ display: 'flex', gap: '24px', borderBottom: '1px solid var(--border)', marginBottom: '24px' }}>
                        {['overview', 'users'].map(tab => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                style={{
                                    background: 'none', border: 'none', padding: '0 0 12px 0', cursor: 'pointer',
                                    fontSize: '14px', fontWeight: '500', textTransform: 'capitalize',
                                    color: activeTab === tab ? 'var(--text)' : 'var(--text-muted)',
                                    borderBottom: activeTab === tab ? '2px solid var(--accent)' : '2px solid transparent',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {tab === 'users' ? 'Manage Users' : 'Overview'}
                            </button>
                        ))}
                    </div>

                    {/* TAB: OVERVIEW */}
                    {activeTab === 'overview' && (
                        <div className="page-fade-in">
                            <div className="stats-grid">
                                <div className="card stat-card">
                                    <p className="stat-label">Total Users</p>
                                    <h3 className="stat-value">{totalUsers}</h3>
                                </div>
                                <div className="card stat-card">
                                    <p className="stat-label">Total Campaigns</p>
                                    <h3 className="stat-value">{campaigns.length}</h3>
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '32px' }}>
                                <div onClick={() => navigate('/admin/payouts')} className="card card-hover" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '16px', padding: '24px' }}>
                                    <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(16, 185, 129, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>💸</div>
                                    <div>
                                        <h4 style={{ fontSize: '16px', color: 'var(--text)', marginBottom: '4px' }}>Manage Payouts</h4>
                                        <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Review and approve worker payout requests.</p>
                                    </div>
                                </div>
                                <div onClick={() => navigate('/admin/revenue')} className="card card-hover" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '16px', padding: '24px' }}>
                                    <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(59, 130, 246, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>💰</div>
                                    <div>
                                        <h4 style={{ fontSize: '16px', color: 'var(--text)', marginBottom: '4px' }}>Platform Revenue</h4>
                                        <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>View financial summaries and platform fees.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* TAB: MANAGE USERS */}
                    {activeTab === 'users' && (
                        <div className="page-fade-in">
                            
                            {/* SUB-TABS FOR ROLE */}
                            <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
                                {[
                                    { label: 'All Users', value: '' },
                                    { label: 'Workers', value: 'WORKER' },
                                    { label: 'Businesses', value: 'BUSINESS' }
                                ].map((subTab) => (
                                    <button
                                        key={subTab.value}
                                        onClick={() => { setFilterRole(subTab.value); setSearch(''); setFilterStatus(''); }}
                                        className={filterRole === subTab.value ? 'btn-primary' : 'btn-secondary'}
                                        style={{ padding: '8px 16px', fontSize: '13px' }}
                                    >
                                        {subTab.label}
                                    </button>
                                ))}
                            </div>

                            <div style={{ marginBottom: '24px' }}>
                                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                        <input 
                                            type="text" 
                                            placeholder={`Search ${filterRole === 'WORKER' ? 'workers' : filterRole === 'BUSINESS' ? 'businesses' : 'all users'}...`}
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            style={{ maxWidth: '300px', margin: 0 }}
                                        />
                                        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ maxWidth: '150px', margin: 0 }}>
                                            <option value="">All Status</option>
                                            <option value="active">Active</option>
                                            <option value="banned">Banned</option>
                                        </select>
                                    </div>
                                    <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                                        Showing {totalUsers} {filterRole ? filterRole.toLowerCase() + 's' : 'users'}
                                    </span>
                                </div>
                            </div>

                            <div className="table-container">
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>ID</th>
                                            <th>Email</th>
                                            <th>Role</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="5" style={{ textAlign: 'center', padding: '40px' }}>
                                                    <div className="spinner" style={{ margin: '0 auto', width: '20px', height: '20px', borderTopColor: '#fff' }}></div>
                                                </td>
                                            </tr>
                                        ) : users.length === 0 ? (
                                            <tr>
                                                <td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                                                    No {filterRole ? filterRole.toLowerCase() + 's' : 'users'} found matching your criteria.
                                                </td>
                                            </tr>
                                        ) : (
                                            users.map(u => {
                                                const isDeleted = u.email.includes('deleted_user');
                                                return (
                                                    <tr key={u.id} style={{ opacity: isDeleted ? 0.5 : 1 }}>
                                                        <td style={{ color: 'var(--text-muted)', fontSize: '13px' }}>#{u.id}</td>
                                                        <td style={{ fontWeight: '500', color: isDeleted ? 'var(--text-muted)' : 'var(--text)' }}>
                                                            {u.email}
                                                        </td>
                                                        <td>
                                                            <span className={`badge ${u.role === 'ADMIN' ? 'badge-error' : u.role === 'BUSINESS' ? 'badge-warning' : ''}`}>
                                                                {u.role}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className={`badge ${u.enabled ? 'badge-success' : 'badge-error'}`}>
                                                                {u.enabled ? 'Active' : (isDeleted ? 'Deleted' : 'Banned')}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                                {u.role !== 'ADMIN' && !isDeleted && (
                                                                    <>
                                                                        <button 
                                                                            onClick={() => openBanModal(u)} 
                                                                            className="btn-secondary" 
                                                                            style={{ padding: '6px 12px', fontSize: '12px' }}
                                                                        >
                                                                            {u.enabled ? 'Ban' : 'Unban'}
                                                                        </button>
                                                                        <button 
                                                                            onClick={() => handleDeleteUser(u.id)} 
                                                                            className="btn-danger" 
                                                                            style={{ padding: '6px 12px', fontSize: '12px' }}
                                                                        >
                                                                            Delete
                                                                        </button>
                                                                    </>
                                                                )}
                                                                {isDeleted && (
                                                                    <span style={{ fontSize: '12px', color: 'var(--text-muted)', padding: '6px 0' }}>Anonymized</span>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </main>
            </div>

            {/* BAN/UNBAN MODAL */}
            {showBanModal && selectedUser && (
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(8px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '20px' }} onClick={() => setShowBanModal(false)}>
                    <div className="card" style={{ width: '100%', maxWidth: '450px', padding: '32px' }} onClick={(e) => e.stopPropagation()}>
                        <h3 style={{ fontSize: '20px', marginBottom: '8px', color: selectedUser.enabled ? 'var(--error)' : 'var(--success)' }}>
                            {selectedUser.enabled ? 'Ban User' : 'Unban User'}
                        </h3>
                        <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '24px' }}>
                            Are you sure you want to {selectedUser.enabled ? 'ban' : 'unban'} <strong style={{ color: 'var(--text)' }}>{selectedUser.email}</strong>?
                        </p>

                        {selectedUser.enabled && (
                            <div className="form-field">
                                <label className="form-label">Reason for Ban (Required)</label>
                                <textarea
                                    placeholder="e.g., Fraudulent activity, multiple fake submissions..."
                                    value={banReason}
                                    onChange={(e) => setBanReason(e.target.value)}
                                    style={{ minHeight: '80px' }}
                                />
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                            <button onClick={() => setShowBanModal(false)} className="btn-secondary" disabled={processing}>
                                Cancel
                            </button>
                            <button 
                                onClick={handleToggleBan} 
                                className={selectedUser.enabled ? 'btn-danger' : 'btn-primary'} 
                                disabled={processing}
                            >
                                {processing ? 'Processing...' : `Confirm ${selectedUser.enabled ? 'Ban' : 'Unban'}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminDashboard;